import { MoreVert as MoreVertIcon, Restore as RestoreIcon } from "@mui/icons-material";
import { Box, Chip, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography, useTheme } from "@mui/material";
import { differenceInDays, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import React from "react";
import { globalBackgroundFire, globalColorTextFire, globalFormatCurrencyBRL, globalFormatDates, globalMaskDocument } from "../../../shared/utils";
import { globalTranslateFireStatusPolices } from "../../../shared/utils/globalTranslateFire";
import { HtmlTooltip } from "../../../infra/components/tooltipsCustom/tooltipsCuston";
import { TokioMarineLogo } from "../icons/TokioMarineLogo";
import { PoliceEvent } from "../types/Polices";

export type PoliceTab = "dados" | "coberturas" | "documentos";

const HEADERS = ["Criação", "Seguradora", "Imobiliária", "Inquilino", "Endereço", "Vigência", "Status", "Prêmio", "Ações"] as const;

interface Props {
	rows: PoliceEvent[];
	totalCount: number;
	page: number;
	rowsPerPage: number;
	onPageChange: (newPage: number) => void;
	onRowsPerPageChange: (value: number) => void;
	onOpenPolice?: (id: string, tab?: PoliceTab) => void; // Adaptado para Apólice
	onOpenMenu: (event: React.MouseEvent<HTMLElement>, row: PoliceEvent) => void;
}

// Mantendo o renderStatusChip, mas você pode precisar de uma lógica de tradução de Status específica para Apólices.
function renderStatusChip(status: string) {
	const label = globalTranslateFireStatusPolices(status); // Substitua por globalTranslatePoliceStatus se existir
	const bg = globalBackgroundFire(status); // Substitua por globalBackgroundPolice se existir
	const color = globalColorTextFire(status); // Substitua por globalColorTextPolice se existir
	return (
		<Chip
			size="medium"
			label={label}
			sx={{
				color,
				fontWeight: 500,
				background: bg,
				border: "0.5px solid #f1f1f1",
			}}
		/>
	);
}

export const TablePolices: React.FC<Props> = ({ rows, totalCount, page, rowsPerPage, onPageChange, onRowsPerPageChange, onOpenPolice, onOpenMenu }) => {
	return (
		<TableContainer
			component={Paper}
			variant="elevation"
			sx={{
				mt: 1,
				width: "96%",
				px: { xs: 1, sm: 3 },
				py: { xs: 2, sm: 3 },
				ml: 1,
				borderRadius: 2,
				overflow: "hidden",
			}}
		>
			<Table size="small" stickyHeader aria-label="Tabela de Apólices">
				<TableHead>
					<TableRow sx={{ backgroundColor: "grey.100" }}>
						{HEADERS.map((header, i) => (
							<TableCell key={header} align={i === HEADERS.length - 1 ? "right" : "left"}>
								<Typography variant="button">{header}</Typography>
							</TableCell>
						))}
					</TableRow>
				</TableHead>

				<TableBody>
					{rows.map((row) => (
						<TableRow key={row.id} hover onClick={() => onOpenPolice?.(row.id)} style={{ cursor: "pointer" }}>
							{/* Criação */}
							<TableCell>
								<Typography>{globalFormatDates(row.createdAt)}</Typography>
								<Typography variant="body2" color="text.secondary">
									{row.createdAt ? format(new Date(row.createdAt), "HH:mm:ss", { locale: ptBR }) : ""}
								</Typography>
							</TableCell>

							{/* Seguradora */}
							<TableCell>
								{row.insuranceCompany?.name?.toLowerCase().includes("tokio") ? (
									<Box mt={0.5}>
										{/* Certifique-se de que o componente TokioMarineLogo está disponível */}
										<TokioMarineLogo />
									</Box>
								) : (
									<Typography>{row.insuranceCompany?.name}</Typography>
								)}
							</TableCell>

							{/* Imobiliária */}
							<TableCell>
								<Typography>{row.client?.name}</Typography>
							</TableCell>

							{/* Inquilino */}
							<TableCell>
								<Typography>{row.tenant_name}</Typography>
								<Typography variant="body2" color="text.secondary">
									{globalMaskDocument(row.tenant_document)}
								</Typography>
							</TableCell>

							{/* Endereço */}
							<TableCell>
								<HtmlTooltip
									title={`${row.address_street}, ${row.address_number}${row.address_complement ? ` - ${row.address_complement}` : ""}`}
									arrow
									placement="top"
								>
									<Typography
										noWrap
										sx={{
											maxWidth: 200,
											cursor: "default",
											display: "inline-block",
										}}
									>
										{`${row.address_street}, ${row.address_number}${row.address_complement ? ` - ${row.address_complement}` : ""}`}
									</Typography>
								</HtmlTooltip>

								<Typography variant="body2" color="text.secondary">
									{row.address_city} - {row.address_state}
								</Typography>
							</TableCell>

							{/* Vigência (Campo novo) */}
							{/* Vigência (Campo novo) */}
							<TableCell>
								<Typography variant="body2" color="text.primary">
									{globalFormatDates(row.validity_start || "")}
								</Typography>
								<Typography variant="body2" color="error">
									{globalFormatDates(row.validity_end || "")}
								</Typography>
							</TableCell>

							{/* Status */}
							<TableCell>
								<Stack direction="row" alignItems="center" spacing={1}>
									<Box>
										{renderStatusChip(row.status || "pendente")}
										{(() => {
											if (!row.validity_end) return null;
											const days = differenceInDays(new Date(row.validity_end), new Date());
											if (days < 30 && days >= 0) {
												return (
													<Typography variant="caption" display="block" color="error" sx={{ mt: 0.5, fontWeight: 500 }}>
														Expira em {days} dias
													</Typography>
												);
											}
											return null;
										})()}
									</Box>
									{row.renovatedAt && (
										<HtmlTooltip
											title={
												<Typography variant="body2">
													Apólice renovada em <strong>{globalFormatDates(row.renovatedAt)}</strong>
												</Typography>
											}
											arrow
											placement="top"
										>
											<RestoreIcon color="action" fontSize="small" />
										</HtmlTooltip>
									)}
								</Stack>
							</TableCell>

							{/* Prêmio (Valor) */}
							<TableCell align="center">
								<Typography sx={{ fontWeight: 600, fontSize: "1rem" }}>{globalFormatCurrencyBRL(row.insurance_prize)}</Typography>
								<Typography variant="caption" color="text.secondary">
									({row.installment_number}x) {globalFormatCurrencyBRL(String(Number(row.insurance_prize) / (row.installment_number || 1)))}
								</Typography>
							</TableCell>

							{/* Ações */}
							<TableCell align="right" onClick={(e) => e.stopPropagation()}>
								<IconButton aria-label={`Ações da apólice ${row.id}`} onClick={(e) => onOpenMenu(e, row)}>
									<MoreVertIcon />
								</IconButton>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
			<Box display="flex" justifyContent="flex-end" alignItems="center" mt={2} pr={2}>
				<TablePagination
					component="div"
					count={totalCount}
					page={page}
					rowsPerPage={rowsPerPage}
					onPageChange={(_, newPage) => onPageChange(newPage)}
					onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
					rowsPerPageOptions={[10, 25, 50]}
					labelRowsPerPage="Linhas por página:"
					labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
				/>
			</Box>
		</TableContainer>
	);
};
